import { useEffect, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'lumiere_exit_seen_v1';

export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { return false; }
  });
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (seen) return;
    let triggered = false;

    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setOpen(true);
    };

    // Desktop: when mouse leaves towards the top of the viewport
    const onMouseOut = (e) => {
      if (e.relatedTarget || e.toElement) return;
      if (e.clientY <= 0) trigger();
    };

    // Mobile / fallback: trigger after 25s on the site
    const timer = setTimeout(trigger, 25000);

    document.addEventListener('mouseout', onMouseOut);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      clearTimeout(timer);
    };
  }, [seen]);

  const close = () => {
    setOpen(false);
    setSeen(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Welcome to Lumière. Code GLOW10 is yours.');
      setCopied(false);
    } catch (err) {
      // Allow code to be visible even if API fails — UX should not block the offer
      toast.error('We could not save your email, but your code is below.');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText('GLOW10');
      setCopied(true);
      toast.success('Code copied to clipboard.');
    } catch (e) {
      toast.error('Could not copy. Code: GLOW10');
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-6 bg-lum-text/55 backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="bg-lum-bg max-w-lg w-full shadow-2xl overflow-hidden animate-fade-up relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="exit-intent-close"
          onClick={close}
          aria-label="close"
          className="absolute top-4 right-4 z-20 text-lum-text2 hover:text-lum-text p-2 bg-lum-bg/80 rounded-full"
        >
          <X size={22} />
        </button>

        <div className="aspect-[5/3] bg-gradient-to-br from-lum-bg2 to-lum-bg3 relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1581182815808-b6eb627a8798?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Glowing skin"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-lum-bg/95 via-lum-bg/10 to-transparent pointer-events-none" />
        </div>

        <div className="px-8 py-8 -mt-12 relative">
          <div className="inline-flex items-center gap-2 bg-lum-sale text-lum-bg px-3 py-1 text-[10px] uppercase tracking-[0.22em] mb-4">
            <Sparkles size={12} /> Wait, don&apos;t leave
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
            10% off your<br/>first ritual.
          </h2>
          <p className="text-sm text-lum-text2 mt-3">
            Drop your email — we&apos;ll send rituals, tips and the code <span className="text-lum-text">GLOW10</span> for 10% off.
          </p>

          <form onSubmit={submit} className="mt-6 flex items-end gap-3">
            <input
              data-testid="exit-intent-email"
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-line flex-1"
            />
            <button
              data-testid="exit-intent-submit"
              disabled={submitting}
              className="btn-primary whitespace-nowrap"
            >
              {submitting ? '...' : 'Claim 10%'}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-between p-3 border border-dashed border-lum-text bg-lum-bg2/60">
            <span className="text-sm">
              <span className="text-[10px] uppercase tracking-[0.22em] text-lum-text2 block mb-1">Your code</span>
              <span className="font-serif-display text-2xl tracking-wider">GLOW10</span>
            </span>
            <button
              type="button"
              onClick={copy}
              data-testid="exit-intent-copy-code"
              className="text-xs uppercase tracking-[0.22em] underline underline-offset-4"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <button
            data-testid="exit-intent-decline"
            onClick={close}
            className="block mx-auto mt-5 text-xs uppercase tracking-[0.22em] text-lum-muted hover:text-lum-text"
          >
            No thanks, I&apos;ll pay full price
          </button>
        </div>
      </div>
    </div>
  );
};
